var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { Grid, Paper, makeStyles, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { getAbbreviationByCountry } from './data/countries';
import { getAbbreviationByProvince } from './data/ca-provinces';
import { getAbbreviationByState } from './data/us-states';
import { LicenseAlert } from './components/LicenseAlert';
import { BDSProvider } from './bds-mui-theme';
import { dummyMixpanel, MixpanelProvider } from './hooks/useMixpanel';
import { LicenseComplianceErrorBoundary } from './components/LicenseComplianceErrorBoundary';
import { PaidLicensesMetricCard } from './components/PaidLicensesMetricCard';
import defaultAdapter from './adapters/defaultAdapter';
import FilterBar from './components/filter/FilterBar';
import InfoIcon from '@material-ui/icons/Info';
import LicenseComplianceTable from './components/LicenseComplianceTable';
import PaginationBar from './components/filter/PaginationBar';
import React, { useEffect } from 'react';
import useAPI from './hooks/useAPI';
import UsedLicensesMetricCard from './components/metrics/UsedLicensesMetricCard';
var useStyles = makeStyles(function () { return ({
    root: {
        fontFamily: 'Roboto, sans-serif',
        maxWidth: '840px'
    },
    header: {
        marginBottom: '16px'
    },
    tableWrapper: {
        padding: '24px 32px'
    },
    licenseRequestedAlert: {
        display: 'flex',
        alignItems: 'center'
    }
}); });
export var LCUI_REQUEST_KEY = 'lcui-request-key';
/**
 * @typedef LicenseComplianceProps
 * @property {string} urlPrefix - This value will be prefixed onto all nextwork calls.
 * This is determined by the consuming product, not this component.

 * @property {LicenseComplianceDataAdapter} dataAdapter - Data dapters tell the component how to fetch data from the API.
 * The default adapter is used if none is provided. Providers are only intended to be supplied for testing.
 *
 * @property {LicenseComplianceMixpanelSettings} mixpanelOptions - Options for sending events to mixpanel.
 */
export function LicenseCompliance(_a) {
    var _b, _c;
    var dataAdapter = _a.dataAdapter, mixpanelOptions = _a.mixpanelOptions, serial = _a.serial, urlPrefix = _a.urlPrefix;
    var classes = useStyles();
    if (!mixpanelOptions.disableMixpanel && !mixpanelOptions.mixpanel) {
        if (!window.mixpanel) {
            throw new Error('Mixpanel is required, but was not provided as a parameter and does not exist on window.');
        }
    }
    var _d = !mixpanelOptions.disableMixpanel
        ? mixpanelOptions
        : { mixpanel: dummyMixpanel(), productPrefix: 'BDS_DEV' }, mixpanel = _d.mixpanel, productPrefix = _d.productPrefix;
    // --- BEGIN STATE ---
    // Adapters are usually passed in for testing, but they are also an
    // escape hatch to supply custom functionality around data fetching.
    var _e = __read(React.useState(dataAdapter
        ? dataAdapter
        : defaultAdapter({
            productIdentifier: mixpanelOptions.productPrefix,
            serial: serial,
            urlPrefix: urlPrefix
        })), 1), adapter = _e[0];
    var _f = __read(React.useState(true), 2), doGetUsers = _f[0], setDoGetUsers = _f[1];
    var _g = __read(React.useState(false), 2), doRequestLicenses = _g[0], setDoRequestLicenses = _g[1];
    // This is a reduced list of the types of licenses that are in the received dataset.
    var _h = __read(React.useState([]), 2), licenseOptions = _h[0], setLicenseOptions = _h[1];
    var _j = __read(React.useState(localStorage.getItem(LCUI_REQUEST_KEY) === 'true'), 2), licenseRequestSent = _j[0], setLicenseRequestSent = _j[1];
    var _k = __read(React.useState({
        search: '',
        licenseTypes: [],
        sortDesc: false
    }), 2), filterState = _k[0], setFilterState = _k[1];
    var _l = __read(React.useState({
        currentPage: 1,
        pageSize: 10,
        itemCount: 0
    }), 2), paginationState = _l[0], setPaginationState = _l[1];
    // --- BEGIN EFFECTS ---
    var detailsState = useAPI(function () { return adapter.getDetails(); }, [adapter]);
    var usersRequest = useAPI(doGetUsers && (function () { return adapter.getUsers(); }), [adapter, doGetUsers], function () {
        setDoGetUsers(false);
    });
    var licenseRequest = useAPI(!!doRequestLicenses && (function () { return adapter.requestLicenses(doRequestLicenses); }), [doRequestLicenses], function (_a) {
        var result = _a.result, status = _a.status;
        setDoRequestLicenses(false);
        if (result === true) {
            localStorage.setItem(LCUI_REQUEST_KEY, 'true');
            setLicenseRequestSent(true);
        }
    });
    useEffect(function () {
        if (!mixpanel)
            return;
        // Ensure mixpanel is defined before calling track
        if (mixpanel && typeof mixpanel.track === 'function') {
            mixpanel.track("".concat(productPrefix, " Licenses pageview"));
        }
    }, [mixpanel, productPrefix]);
    useEffect(function () {
        if (!usersRequest.result)
            return;
        setPaginationState(__assign(__assign({}, paginationState), { itemCount: usersRequest.result.length }));
        // Once there are users, we must pre-process them to get the list of license types,
        // and to set up pagination state.
        var uniqueLicenseTypes = usersRequest.result.reduce(function (acc, user) {
            user.licenses.forEach(function (license) { return acc.add(license); });
            return acc;
        }, new Set());
        setLicenseOptions(Array.from(uniqueLicenseTypes));
    }, [usersRequest.result]);
    // --- BEGIN HANDLERS ---
    var handleSearchChange = function (event) {
        setFilterState(__assign(__assign({}, filterState), { search: event.target.value }));
        setPaginationState(__assign(__assign({}, paginationState), { currentPage: 1 }));
    };
    var handleLicenseChange = function (event) {
        var licenseTypes = [];
        var newValue = event.target.value;
        if (newValue.includes('Select All') && !filterState.licenseTypes.includes('Select All')) {
            licenseOptions.forEach(function (license) { return licenseTypes.push(license); });
            licenseTypes.push('Select All');
        }
        else {
            licenseTypes = newValue;
        }
        // If Select All has been unselected
        if (filterState.licenseTypes.includes('Select All') && !licenseTypes.includes('Select All')) {
            licenseTypes = [];
        }
        // If Select All previously checked, but something else was unchecked
        if (newValue.includes('Select All') &&
            newValue.length !== licenseOptions.length + 1 &&
            filterState.licenseTypes.includes('Select All')) {
            licenseTypes = newValue.filter(function (license) { return license !== 'Select All'; });
        }
        var newState = __assign(__assign({}, filterState), { licenseTypes: licenseTypes });
        setFilterState(newState);
    };
    var handleSortClick = function () {
        setFilterState(__assign(__assign({}, filterState), { sortDesc: !filterState.sortDesc }));
        setPaginationState(__assign(__assign({}, paginationState), { currentPage: 1 }));
    };
    var handlePageChange = function (e, page) {
        setPaginationState(__assign(__assign({}, paginationState), { currentPage: page }));
    };
    var handlePageSizeChange = function (e) {
        setPaginationState(__assign(__assign({}, paginationState), { currentPage: 1, pageSize: e.target.value }));
    };
    var handleFilter = function (filteredUsers) {
        setPaginationState(__assign(__assign({}, paginationState), { itemCount: filteredUsers.length }));
    };
    var getStatesOrProvince = function (country, stateOrProvince) {
        switch (true) {
            case country === 'United States':
                return getAbbreviationByState(stateOrProvince);
            case country === 'Canada':
                return getAbbreviationByProvince(stateOrProvince);
            default:
                return stateOrProvince;
        }
    };
    var handleLicensesRequest = function (formData) {
        // validated form data
        var validatedFormData = __assign(__assign(__assign({ email: formData.email, firstName: formData.firstName, lastName: formData.lastName, company: formData.company, phone: formData.phone }, (formData.country ? { country: getAbbreviationByCountry(formData.country) } : {})), (formData.state && formData.country ? { state: getStatesOrProvince(formData.country, formData.state) } : {})), (formData.postalCode ? { postalCode: formData.postalCode } : {}));
        setDoRequestLicenses(validatedFormData);
    };
    var handleUserReloadRequest = function () {
        setDoGetUsers(true);
    };
    // --- BEGIN RENDER ---
    var alert;
    if ((detailsState === null || detailsState === void 0 ? void 0 : detailsState.result) && detailsState.status !== 'error') {
        if (licenseRequestSent) {
            alert = (React.createElement(Grid, { item: true, xs: 12 },
                React.createElement(Alert, { icon: React.createElement(InfoIcon, null), color: "info", className: classes.licenseRequestedAlert },
                    React.createElement(Typography, { variant: "body2" }, "Additional licenses were requested. A Barracuda representative will contact you to discuss your license needs within 48 hours."))));
        }
        else if (detailsState.result.paidLicenses < detailsState.result.licensesInUseCount) {
            alert = (React.createElement(Grid, { item: true, xs: 12 },
                React.createElement(LicenseAlert, { detailsState: detailsState, onLicensesRequest: handleLicensesRequest, licenseRequestState: licenseRequest })));
        }
    }
    return (React.createElement(BDSProvider, { noCSSReset: true },
        React.createElement("div", { id: "license-compliance-root", className: classes.root, "data-testid": "lcui-component" },
            React.createElement(LicenseComplianceErrorBoundary, null,
                React.createElement(MixpanelProvider, { productPrefix: productPrefix, mixpanel: mixpanel },
                    React.createElement(Typography, { variant: "h2", className: classes.header }, "Licenses"),
                    React.createElement(Grid, { container: true, spacing: 4 },
                        React.createElement(Grid, { item: true, xs: 12 }, alert),
                        React.createElement(Grid, { item: true, xs: 12, sm: 5 },
                            React.createElement(PaidLicensesMetricCard, { requestState: detailsState })),
                        React.createElement(Grid, { item: true, xs: 12, sm: 5 },
                            React.createElement(UsedLicensesMetricCard, { requestState: detailsState })),
                        React.createElement(Grid, { item: true, xs: 12, sm: 2 }),
                        React.createElement(Grid, { item: true, xs: 12 },
                            React.createElement(Paper, { elevation: 2, className: classes.tableWrapper },
                                ((_b = usersRequest.result) === null || _b === void 0 ? void 0 : _b.length) ? (React.createElement(FilterBar, { filterState: filterState, licenseOptions: licenseOptions, onSortClick: handleSortClick, onLicenseChange: handleLicenseChange, onSearchChange: handleSearchChange, users: usersRequest.result })) : null,
                                React.createElement(LicenseComplianceTable, { userRequestState: usersRequest, filterState: filterState, onFilter: handleFilter, onReloadUsersClick: handleUserReloadRequest, paginationState: paginationState }),
                                ((_c = usersRequest.result) === null || _c === void 0 ? void 0 : _c.length) ? (React.createElement(PaginationBar, { paginationState: paginationState, onChange: handlePageChange, onSizeChange: handlePageSizeChange })) : null))))))));
}
//# sourceMappingURL=LicenseCompliance.js.map